// import React from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import SustainabilityScoreCircle from 'kosmos/components/SustainabilityScoreCircle';
import SubSection from './_subSection';
import { smartGovernancePrimaryColor } from 'kosmosConfig';
import { date2str } from 'utils/date';
import { useTranslation } from 'react-i18next';

const PREFIX = '_indicativeRegulatoryReport';

const classes = {
  chartContainer: `${PREFIX}-chartContainer`,
  esgBreakdownContainer: `${PREFIX}-esgBreakdownContainer`,
  chart: `${PREFIX}-chart`,
  legends: `${PREFIX}-legends`,
  legend: `${PREFIX}-legend`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.chartContainer}`]: {
    display: 'flex',
    flexDirection: 'row',
  },
  [`& .${classes.esgBreakdownContainer}`]: {
    width: 170,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.chart}`]: {
    margin: 5,
    flex: 1,
    textAlign: 'center',
    alignSelf: 'center',
  },
  [`& .${classes.legends}`]: {
    width: 100,
    height: '100%',
    textAlign: 'center',
  },
  [`& .${classes.legend}`]: {
    height: 150,
    lineHeight: '150px',
    fontSize: 30,
    fontWeight: 'bold',
    textAlign: 'center',
  },
}));

export interface IScore {
  kosmosEsgScore: number;
  kosmosEsgEnvironmentScore?: number;
  kosmosEsgSocialScore?: number;
  kosmosEsgGovernanceScore?: number;
  reportDate?: string;
}

interface IProps {
  automatedScore?: IScore;
  smartGovernanceScore?: IScore;
}

export default function ScoreCharts(props: IProps): JSX.Element {
  const { automatedScore, smartGovernanceScore } = props;
  const { t } = useTranslation('default');

  const renderDimensionScore = (score: number) => {
    return (
      <div className={classes.chart}>
        <SustainabilityScoreCircle prop={t('score')} score={score} size="tiny" />
      </div>
    );
  };

  const renderESGScores = (e = 0, s = 0, g = 0) => {
    return (
      <div className={classes.esgBreakdownContainer}>
        {renderDimensionScore(e)}
        {renderDimensionScore(s)}
        {renderDimensionScore(g)}
      </div>
    );
  };

  const renderESGLegends = () => {
    return (
      <div className={classes.legends}>
        <div className={classes.legend}>{t('e')}</div>
        <div className={classes.legend}>{t('s')}</div>
        <div className={classes.legend}>{t('g')}</div>
      </div>
    );
  };

  const renderOverallScore = (score: number) => {
    return <SustainabilityScoreCircle prop={t('score')} score={score} width={350} height={350} />;
  };

  const renderAutoScoredDesc = () => {
    return (
      <>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: '#6bcec1',
            display: 'inline',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          {'automated-esg'}&nbsp;
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: 'inline',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          {t('score')}
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {t('lastest-report-generated')}
        </Typography>
        <Typography variant="body1" gutterBottom sx={{}}>
          {date2str(automatedScore?.reportDate, 'DD/MM/YYYY')}
        </Typography>
      </>
    );
  };

  const renderSGScoreDesc = () => {
    return (
      <>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: smartGovernancePrimaryColor,
            display: 'inline',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          {t('smart-governance')}&nbsp;
        </Typography>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            display: 'inline',
            fontWeight: 'bold',
            textTransform: 'uppercase',
          }}
        >
          {t('score')}
        </Typography>
        <Typography
          variant="body1"
          gutterBottom
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {t('completed')}
        </Typography>
        <Typography variant="body1" gutterBottom sx={{}}>
          {date2str(smartGovernanceScore?.reportDate, 'DD/MM/YYYY')}
        </Typography>
      </>
    );
  };

  return (
    <Root>
      <SubSection isBold title={t('overall-score')}>
        <div className={classes.chartContainer}>
          <div className={classes.chart}>
            {renderOverallScore(automatedScore?.kosmosEsgScore || 0)}
            {renderAutoScoredDesc()}
          </div>
          {renderESGScores(
            automatedScore?.kosmosEsgEnvironmentScore,
            automatedScore?.kosmosEsgSocialScore,
            automatedScore?.kosmosEsgGovernanceScore,
          )}
          {renderESGLegends()}
          {renderESGScores(
            smartGovernanceScore?.kosmosEsgEnvironmentScore,
            smartGovernanceScore?.kosmosEsgSocialScore,
            smartGovernanceScore?.kosmosEsgGovernanceScore,
          )}
          <div className={classes.chart}>
            {renderOverallScore(smartGovernanceScore?.kosmosEsgScore || 0)}
            {renderSGScoreDesc()}
          </div>
        </div>
      </SubSection>
    </Root>
  );
}
